import { VisionButton, VisionInput } from "@/components/VisionUI"
import { Field } from "@/components/ui/field"
import { Box, Heading } from "@chakra-ui/react"
import { useMutation } from "@tanstack/react-query"
import { type SubmitHandler, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"

import { type ApiError, type UpdatePassword, UsersService } from "@/client"
import useCustomToast from "@/hooks/useCustomToast"
import { useSemanticColors } from "@/hooks/useSemanticColors"
import { handleError } from "@/utils"

interface UpdatePasswordForm extends UpdatePassword {
  confirm_password: string
}

const ChangePassword = () => {
  const { t } = useTranslation(["settings", "common"])
  const sc = useSemanticColors()
  const { showSuccessToast } = useCustomToast()
  const {
    register,
    handleSubmit,
    reset,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<UpdatePasswordForm>({
    mode: "onBlur",
    criteriaMode: "all",
  })

  const mutation = useMutation({
    mutationFn: (data: UpdatePassword) =>
      UsersService.updatePasswordMe({ requestBody: data }),
    onSuccess: () => {
      showSuccessToast(t("settings:password.updated"))
      reset()
    },
    onError: (err: ApiError) => {
      handleError(err)
    },
  })

  const onSubmit: SubmitHandler<UpdatePasswordForm> = async (data) => {
    mutation.mutate(data)
  }

  return (
    <>
      <Heading size="md" color={sc.heading} mb={4}>
        {t("settings:password.title")}
      </Heading>
      <Box as="form" onSubmit={handleSubmit(onSubmit)}>
        <Field
          label={t("settings:password.current")}
          required
          invalid={!!errors.current_password}
          errorText={errors.current_password?.message}
        >
          <VisionInput
            {...register("current_password", {
              required: t("common:validation.passwordRequired"),
            })}
            placeholder={t("common:placeholder.password")}
            type="password"
            variant="glass"
            autoComplete="current-password"
          />
        </Field>
        <Field
          mt={4}
          label={t("settings:password.new")}
          required
          invalid={!!errors.new_password}
          errorText={errors.new_password?.message}
        >
          <VisionInput
            {...register("new_password", {
              required: t("common:validation.passwordRequired"),
              minLength: {
                value: 8,
                message: t("common:validation.passwordMinLength"),
              },
            })}
            placeholder={t("common:placeholder.password")}
            type="password"
            variant="glass"
            autoComplete="new-password"
          />
        </Field>
        <Field
          mt={4}
          label={t("settings:password.confirm")}
          required
          invalid={!!errors.confirm_password}
          errorText={errors.confirm_password?.message}
        >
          <VisionInput
            {...register("confirm_password", {
              required: t("common:validation.passwordConfirmRequired"),
              validate: (value) =>
                value === getValues().new_password ||
                t("common:validation.passwordMismatch"),
            })}
            placeholder={t("common:placeholder.password")}
            type="password"
            variant="glass"
            autoComplete="new-password"
          />
        </Field>
        <VisionButton
          variant="brand"
          mt={4}
          type="submit"
          loading={isSubmitting}
        >
          {t("common:actions.save")}
        </VisionButton>
      </Box>
    </>
  )
}
export default ChangePassword
